import { Box, useTheme, Typography } from '@mui/material';
import { BaseAssetProps } from '../types';
import { AssetWrapper } from '../assetWrapper/AssetWrapper';
import { useWaterData } from 'src/hooks/featureHooks';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetAssetByIdQuery } from 'src/hooks/reactQueryApiHooks';
import { AssetNameLabel } from '../../assetNameLabel/AssetNameLabel';

type Props = BaseAssetProps & {
  isLevelShape?: boolean;
};

export const Well = ({ selected, width, height, data, isLevelShape, id }: Props) => {
  const theme = useTheme();
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const widthOfElement = width ?? 180;
  const heightOfElement = height ?? 380;

  const tankHeight = heightOfElement * 0.99;

  const siteId = data?.siteId;
  const assetId = data?.assetId;

  const { asset } = useGetAssetByIdQuery({
    customerId: customerId!,
    siteId,
    assetId,
  });

  const { waterLevel, unit, height: eneteredHeight } = useWaterData({ asset });

  const waterHeightPx = ((waterLevel ?? 0) / eneteredHeight!) * tankHeight;

  const labelBottom = Math.min(waterHeightPx, tankHeight - 58);

  const waterLevelPercentage = (waterLevel! / eneteredHeight!) * 100;

  return (
    <AssetWrapper
      selected={selected}
      width={widthOfElement}
      height={heightOfElement}
      id={id}
      key={`${data?.siteId}-${data?.assetId}`}
    >
      <Box
        data-sm="wet-well-widget"
        sx={{
          position: 'relative',
          width: widthOfElement,
          height: heightOfElement,
          border: isLevelShape ? 'none' : `0.1px solid ${theme.palette.grey[400]}`,
          background: '#f4f4f7',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: `${(heightOfElement * waterLevelPercentage) / 100}px`,
            backgroundColor: '#cbdef7',
            transition: 'height 0.3s ease',
          }}
        />

        {waterLevel !== null && (
          <Typography
            variant="caption"
            data-sm="well-value"
            sx={{
              position: 'absolute',
              bottom: `${labelBottom}px`,
              left: '50%',
              transform: 'translateX(-50%)',
              fontWeight: 500,
              fontSize: `${Math.max(widthOfElement, heightOfElement) * 0.035}px`,
              px: 0.5,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            {waterLevel} {unit}
          </Typography>
        )}
      </Box>

      {data?.isDisplayName && (
        <AssetNameLabel
          assetName={asset?.assetName}
          widthOfElement={widthOfElement}
          heightOfElement={heightOfElement}
        />
      )}
    </AssetWrapper>
  );
};
